import { BASE_URL } from './api';

const getToken = () => {
  return localStorage.getItem('study-app-token');
};

const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const errorMessage = errorData.detail || errorData.message || `Server error: ${response.status}`;
    
    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem('study-app-token');
      localStorage.removeItem('study-app-user');
      window.location.href = '/login';
    }
    
    throw new Error(errorMessage);
  }
  
  return response.json();
};

// Question Management APIs

/**
 * Get paginated list of questions with optional filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 20)
 * @param {string} params.search - Search query for question text
 * @param {string} params.topic - Filter by topic
 * @param {string} params.difficulty - Filter by difficulty (easy, medium, hard)
 * @param {string} params.question_type - Filter by question type
 * @param {string} params.sort_by - Sort field (id, topic, difficulty)
 * @param {string} params.sort_order - Sort order (asc, desc)
 * @returns {Promise<Object>} Questions list with pagination info
 */
export const getQuestions = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  Object.keys(params).forEach(key => {
    if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
      queryParams.append(key, params[key]);
    }
  });
  
  const url = `${BASE_URL}/admin/questions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Get a single question by ID
 * @param {number} id - Question ID
 * @returns {Promise<Object>} Question details
 */
export const getQuestionById = async (id) => {
  const response = await fetch(`${BASE_URL}/admin/questions/${id}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Create a new question
 * @param {Object} data - Question data
 * @param {string} data.question_text - Question text (required)
 * @param {string} data.question_type - Question type (required)
 * @param {string} data.difficulty - Difficulty level (required)
 * @param {string} data.topic - Topic (required)
 * @param {Array<string>} data.options - Options for multiple choice
 * @param {string} data.correct_answer - Correct answer (required)
 * @param {string} data.explanation - Explanation text
 * @returns {Promise<Object>} Created question
 */
export const createQuestion = async (data) => {
  const response = await fetch(`${BASE_URL}/admin/questions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  
  return handleResponse(response);
};

/**
 * Update an existing question
 * @param {number} id - Question ID
 * @param {Object} data - Updated question data (partial)
 * @returns {Promise<Object>} Update result with updated fields
 */
export const updateQuestion = async (id, data) => {
  const response = await fetch(`${BASE_URL}/admin/questions/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  
  return handleResponse(response);
};

/**
 * Delete a question
 * @param {number} id - Question ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteQuestion = async (id) => {
  const response = await fetch(`${BASE_URL}/admin/questions/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

// Statistics & Metadata APIs

/**
 * Get question statistics summary
 * @returns {Promise<Object>} Statistics including counts by difficulty, type, and topics
 */
export const getQuestionStats = async () => {
  const response = await fetch(`${BASE_URL}/admin/questions/stats/summary`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

/**
 * Get list of all available topics
 * @returns {Promise<Object>} Object with topics array
 */
export const getTopicsList = async () => {
  const response = await fetch(`${BASE_URL}/admin/questions/topics/list`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

// Bulk Operations APIs

/**
 * Bulk import questions from CSV file
 * @param {File} file - CSV file to upload
 * @param {boolean} skipDuplicates - Whether to skip duplicate questions
 * @returns {Promise<Object>} Import results with success, skipped, and failed counts
 */
export const bulkImportQuestions = async (file, skipDuplicates = true) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const token = getToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const url = `${BASE_URL}/admin/questions/bulk-import?skip_duplicates=${skipDuplicates}`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: formData
  });
  
  return handleResponse(response);
};

// Activity Logs APIs

/**
 * Get activity logs with optional filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 20, max: 100)
 * @param {string} params.action - Filter by action type
 * @param {string} params.resource_type - Filter by resource type
 * @param {number} params.admin_user_id - Filter by admin user ID
 * @returns {Promise<Object>} Activity logs with pagination
 */
export const getActivityLogs = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  Object.keys(params).forEach(key => {
    if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
      queryParams.append(key, params[key]);
    }
  });
  
  const url = `${BASE_URL}/admin/activity-logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

// Utility function to parse options from JSON string
export const parseOptions = (optionsString) => {
  if (!optionsString) return [];
  try {
    return JSON.parse(optionsString);
  } catch (e) {
    console.error('Error parsing options:', e);
    return [];
  }
};

// Utility function to format options for API
export const formatOptionsForApi = (optionsArray) => {
  if (!Array.isArray(optionsArray)) return '[]';
  return JSON.stringify(optionsArray);
};
